import { OwaDate, addMilliseconds, differenceInMilliseconds, now, isAfter } from 'owa-datetime';
import type { Todo, Subtodo } from 'owa-todo-types';
import { assertNever } from 'owa-assert';

const HOUR_IN_MILLISECONDS = 1 * 60 * 60 * 1000;

export type ListSort = 'CommittedOrder' | 'OrderDateTime';
type SubtodoListSort = 'OrderDateTime';

/**
 * Gets the property updates needed to create a task at the target index in a list
 * @param targetIndex The index of the desired task insert location
 */
export function getPropertiesForInsert(
    targetIndex: number,
    todoList: Todo[],
    listSort: ListSort
): Partial<Todo> {
    // If there are no todos at all, no properties need to be set
    if (todoList.length === 0) {
        return {};
    }

    // If drop is after last item
    if (targetIndex >= todoList.length) {
        const lastTodo = todoList[todoList.length - 1];
        return getInsertAfterProperties(lastTodo, listSort);
    }
    // If there's no previous item (dropped on index 0)
    if (targetIndex === 0) {
        const firstTodo = todoList[targetIndex];
        return getInsertBeforeProperties(firstTodo, listSort);
    }
    const todoAfter = todoList[targetIndex];
    const todoBefore = todoList[targetIndex - 1];
    return getInsertBetweenProperties(todoBefore, todoAfter, listSort);
}

export function getSubtodoPropertiesForInsert(
    targetIndex: number,
    subtodoList: Subtodo[]
): Partial<Subtodo> {
    if (subtodoList.length === 0) {
        return {};
    }

    const listSort: SubtodoListSort = 'OrderDateTime';
    if (targetIndex >= subtodoList.length) {
        const lastSubtodo = subtodoList[subtodoList.length - 1];
        return getSubtodoInsertAfterProperties(lastSubtodo, listSort);
    }
    if (targetIndex === 0) {
        const firstSubtodo = subtodoList[targetIndex];
        return getSubtodoInsertBeforeProperties(firstSubtodo, listSort);
    }
    const subtodoAfter = subtodoList[targetIndex];
    const subtodoBefore = subtodoList[targetIndex - 1];
    return getSubtodoInsertBetweenProperties(subtodoBefore, subtodoAfter, listSort);
}

function getInsertAfterProperties(todo: Todo, listSort: ListSort) {
    // If drop is after last item, use 1 hour earlier than last item.
    switch (listSort) {
        case 'CommittedOrder':
            return { CommittedOrder: addMilliseconds(todo.CommittedOrder, -HOUR_IN_MILLISECONDS) };
        case 'OrderDateTime':
            return getInsertAfterOrderDateTimeProperty(todo);
        default:
            return assertNever(listSort);
    }
}

function getSubtodoInsertAfterProperties(
    subtodo: Subtodo,
    listSort: SubtodoListSort
): Partial<Subtodo> {
    switch (listSort) {
        case 'OrderDateTime':
            return getInsertAfterOrderDateTimeProperty(subtodo);
        default:
            return assertNever(listSort);
    }
}

function getInsertAfterOrderDateTimeProperty(item: Todo | Subtodo): Partial<Todo | Subtodo> {
    return { OrderDateTime: addMilliseconds(item.OrderDateTime, -HOUR_IN_MILLISECONDS) };
}

function getInsertBeforeProperties(todo: Todo, listSort: ListSort): Partial<Todo> {
    const nowTime = now();
    switch (listSort) {
        case 'CommittedOrder':
            // Use now as the max time, unless the CommittedOrder is after now, then use one hour after the CommittedOrder.
            const maxTime = isAfter(todo.CommittedOrder, nowTime)
                ? addMilliseconds(todo.CommittedOrder, HOUR_IN_MILLISECONDS)
                : nowTime;
            return { CommittedOrder: getDateBetweenDates(maxTime, todo.CommittedOrder) };
        case 'OrderDateTime':
            return getInsertBeforeOrderDateTimeProperty(todo);
        default:
            return assertNever(listSort);
    }
}

function getSubtodoInsertBeforeProperties(
    subtodo: Subtodo,
    listSort: SubtodoListSort
): Partial<Subtodo> {
    switch (listSort) {
        case 'OrderDateTime':
            return getInsertBeforeOrderDateTimeProperty(subtodo);
        default:
            assertNever(listSort);
    }
}

function getInsertBeforeOrderDateTimeProperty(item: Todo | Subtodo): Partial<Todo | Subtodo> {
    // Use now as the max time, unless the OrderDateTime is after now, then use one hour after the OrderDateTime.
    const nowTime = now();
    const maxTime = isAfter(item.OrderDateTime, nowTime)
        ? addMilliseconds(item.OrderDateTime, HOUR_IN_MILLISECONDS)
        : nowTime;

    return { OrderDateTime: getDateBetweenDates(maxTime, item.OrderDateTime) };
}

function getInsertBetweenProperties(
    todoBefore: Todo,
    todoAfter: Todo,
    listSort: ListSort
): Partial<Todo> {
    // Find a date between this task and the previous one.
    switch (listSort) {
        case 'CommittedOrder':
            return {
                CommittedOrder: getDateBetweenDates(
                    todoBefore.CommittedOrder,
                    todoAfter.CommittedOrder
                ),
            };
        case 'OrderDateTime':
            return getInsertBetweenOrderDateTimeProperty(todoBefore, todoAfter);
        default:
            return assertNever(listSort);
    }
}

function getSubtodoInsertBetweenProperties(
    subtodoBefore: Subtodo,
    subtodoAfter: Subtodo,
    listSort: SubtodoListSort
): Partial<Subtodo> {
    switch (listSort) {
        case 'OrderDateTime':
            return getInsertBetweenOrderDateTimeProperty(subtodoBefore, subtodoAfter);
        default:
            return assertNever(listSort);
    }
}

function getInsertBetweenOrderDateTimeProperty(
    itemBefore: Todo | Subtodo,
    itemAfter: Todo | Subtodo
) {
    return {
        OrderDateTime: getDateBetweenDates(itemBefore.OrderDateTime, itemAfter.OrderDateTime),
    };
}

export function getDateBetweenDates(maxDateTime: OwaDate, minDateTime: OwaDate): OwaDate {
    const halfDate = differenceInMilliseconds(maxDateTime, minDateTime) / 2;
    return addMilliseconds(minDateTime, halfDate);
}
